import { Cart } from "@/lib/shopify/types";
import { siteConfig } from "@/lib/seo-config";

export const BAG_ENABLED = process.env.NEXT_PUBLIC_FEATURE_CART === "true";

// Mirrors the Shopify automatic discount ("25% off orders over $80").
// If the Shopify admin setup changes, these must change with it.
// Shipping is intentionally NOT modelled here — it's calculated by Shopify at
// checkout, so the bag shows merchandise totals only.
export const BAG_DISCOUNT_THRESHOLD = 80;
export const BAG_DISCOUNT_PERCENT = 25;

export const DIGITAL_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_DIGITAL_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
export const PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PRINTED_VARIANT_ID ||
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_VARIANT_ID ||
  "";
// Discounted printed book offered as a post-purchase upsell ("Get it printed
// too!" on /comix/unlock). Still fulfilled as a printed book (_product stays
// "printed"); only the variant/price differ from the standalone printed book.
export const UPSELL_PRINTED_VARIANT_ID =
  process.env.NEXT_PUBLIC_SHOPIFY_COMIX_PHYSICAL_20_VARIANT_ID ||
  PRINTED_VARIANT_ID ||
  "";

export function lineAttr(
  line: { attributes?: { key: string; value: string }[] },
  key: string,
): string | undefined {
  return line.attributes?.find((a) => a.key === key)?.value;
}

export function isDigitalLine(line: {
  attributes?: { key: string; value: string }[];
}): boolean {
  return lineAttr(line, "_product") === "digital";
}

export interface BagTotals {
  /** Undiscounted merchandise subtotal — drives the promo progress bar. */
  subtotal: number;
  /** Merchandise total after Shopify's automatic discount. */
  discountedSubtotal: number;
  discountActive: boolean;
  amountToThreshold: number;
  /** 0..1 progress toward the discount threshold. */
  thresholdProgress: number;
  /** Order (merchandise) savings from the automatic discount. */
  totalSavings: number;
  /** Merchandise total shown in the bag. Shipping is added by Shopify at checkout. */
  total: number;
  totalBeforeDiscount: number;
}

export function computeBagTotals(cart: Cart): BagTotals {
  const subtotal = parseFloat(cart.cost.subtotalAmount.amount || "0");
  const discountedSubtotal = parseFloat(cart.cost.totalAmount.amount || "0");
  const discountActive = discountedSubtotal < subtotal - 0.001;

  return {
    subtotal,
    discountedSubtotal,
    discountActive,
    amountToThreshold: Math.max(0, BAG_DISCOUNT_THRESHOLD - subtotal),
    thresholdProgress: Math.min(1, subtotal / BAG_DISCOUNT_THRESHOLD),
    totalSavings: subtotal - discountedSubtotal,
    total: discountedSubtotal,
    totalBeforeDiscount: subtotal,
  };
}

/**
 * Builds the Shopify line-item attributes for a comix run. The webhook
 * (order-paid) and the bag UI both depend on these keys — keep in sync there.
 */
export function buildComixLineAttributes(opts: {
  run: {
    sessionId?: string | null;
    userEmail?: string | null;
    generatedImages?: string[];
    config?: Record<string, any> | null;
  };
  runId: string;
  product: string;
  fallbackSessionId?: string;
  characterPhotoUrls?: string[];
}): { key: string; value: string }[] {
  const { run, runId, product, fallbackSessionId, characterPhotoUrls = [] } = opts;
  const config = (run.config as Record<string, any>) || {};
  const coverImage = config.coverImage as string | undefined;
  const allImages = [
    ...(coverImage ? [coverImage] : []),
    ...(run.generatedImages || []),
  ];
  const activeSessionId = run.sessionId || fallbackSessionId || "";
  const storyId = config.storyId || "";
  const storyTitle = config.storyHeadline || config.storyTemplate || "";
  const baseUrl = siteConfig.url;
  const previewUrl = `${baseUrl}/comix/preview?sessionId=${activeSessionId}&storyId=${storyId}`;

  return [
    { key: "_generated", value: allImages.join(",") },
    { key: "_sessionId", value: activeSessionId },
    { key: "_runId", value: runId },
    { key: "_productType", value: "comix" },
    { key: "_product", value: product },
    { key: "_previewUrl", value: previewUrl },
    ...(run.userEmail ? [{ key: "_customerEmail", value: run.userEmail }] : []),
    ...(storyId ? [{ key: "_storyId", value: storyId }] : []),
    ...(characterPhotoUrls.length > 0
      ? [{ key: "_characterPhotos", value: characterPhotoUrls.join(",") }]
      : []),
    ...(storyTitle ? [{ key: "_title", value: storyTitle }] : []),
    ...(coverImage ? [{ key: "_coverImage", value: coverImage }] : []),
  ];
}

/**
 * Cart line attributes are writable by anyone holding the public Storefront
 * token, so URLs read from them must never reach href/src unsanitized
 * (blocks javascript:, data:, etc.).
 */
export function safeHttpUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return url;
    }
  } catch {
    // not an absolute URL
  }
  return undefined;
}

export function formatUsd(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}
